import { useEffect, useState } from "react";

/**
 * Acompanha `navigator.onLine` em tempo real.
 *
 * O valor inicial é `true` no SSR / antes do mount pra evitar flash de
 * "offline" durante hydration; depois do mount usa o valor real do
 * navegador. Inscreve nos eventos `online`/`offline` do `window`.
 *
 * Nota: `navigator.onLine === false` é confiável (perdeu link), mas
 * `=== true` não garante internet de verdade — só significa "tem alguma
 * rede local". UX deve tratar `false` como sinal forte e `true` como
 * "provavelmente conectado".
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}
